'use client';

import { useEffect, useMemo, useState, type ComponentProps } from 'react';
import { useTranslations } from 'next-intl';
import { Box } from '@terra/ui-styled-system';
import {
  Button,
  ContentCard,
  DomainScopeTable,
  DomainWafStatusIcon,
  WAF_STATUS,
  applyWafStatusesToDomains,
} from '@terra/ui-design-system';
import { Add01Icon } from '@terra/ui-icons';
import { useDomainsWafStatuses } from '../../../hooks/api/useDomainsWafStatuses';
import { useSettingsTabHeaderActions } from '../settingsTabHeaderContext';
import { TargetNoTargetState } from './TargetNoTargetState';

type DomainScopeTableProps = ComponentProps<typeof DomainScopeTable>;

type TargetDomainsPanelProps = {
  hasProjectSelected: boolean;
  isLoading: boolean;
  isReadOnly?: boolean;
  domains: DomainScopeTableProps['domains'];
  discoveredSubdomains: string[];
  applicationUrl: string;
  targetUrl: string | undefined;
  redirectToUrl: DomainScopeTableProps['redirectToUrl'];
  onDomainsChange: DomainScopeTableProps['onDomainsChange'];
};

const resolveHostname = (url: string | undefined): string | null => {
  if (!url) {
    return null;
  }

  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
};

export function TargetDomainsPanel({
  hasProjectSelected,
  isLoading,
  isReadOnly,
  domains,
  discoveredSubdomains,
  applicationUrl,
  targetUrl,
  redirectToUrl,
  onDomainsChange,
}: TargetDomainsPanelProps) {
  const tCommon = useTranslations('designSystem.common');
  const [isAddDomainDialogOpen, setIsAddDomainDialogOpen] = useState(false);
  const appUrlHostname = useMemo(() => resolveHostname(targetUrl), [targetUrl]);
  const domainNames = useMemo(
    () => domains.map((domainEntry) => domainEntry.domain),
    [domains],
  );
  const wafStatuses = useDomainsWafStatuses({ domains: domainNames });

  useEffect(() => {
    if (isReadOnly) {
      return;
    }

    const nextDomains = applyWafStatusesToDomains({
      domains,
      statuses: wafStatuses,
    });

    if (nextDomains) {
      onDomainsChange(nextDomains);
    }
  }, [domains, wafStatuses, isReadOnly, onDomainsChange]);

  const headerActions = useMemo(
    () =>
      hasProjectSelected ? (
        <Button
          size="xs"
          variant="ghost"
          disabled={isReadOnly}
          onClick={() => {
            setIsAddDomainDialogOpen((currentValue) => !currentValue);
          }}
        >
          <Add01Icon size={16} />
          {tCommon('add')}
        </Button>
      ) : null,
    [hasProjectSelected, isReadOnly, tCommon],
  );

  useSettingsTabHeaderActions(headerActions);

  if (!hasProjectSelected) {
    return <TargetNoTargetState descriptionKey="domains" />;
  }

  return (
    <ContentCard minHeight="340px">
      <DomainScopeTable
        domains={domains}
        discoveredSubdomains={discoveredSubdomains}
        appUrlHostname={appUrlHostname}
        primaryApplicationUrl={applicationUrl.trim() || null}
        redirectToUrl={redirectToUrl}
        isLoading={isLoading}
        isReadOnly={isReadOnly}
        tableMaxHeight="50vh"
        onDomainsChange={onDomainsChange}
        isAddDomainDialogOpen={isAddDomainDialogOpen}
        onAddDomainDialogOpenChange={setIsAddDomainDialogOpen}
        renderDomainSuffix={(domain) => (
          <Box flexShrink={0}>
            <DomainWafStatusIcon
              wafStatus={
                wafStatuses[domainNames.indexOf(domain)] ?? WAF_STATUS.idle
              }
            />
          </Box>
        )}
      />
    </ContentCard>
  );
}
